import { Inject, Injectable, signal } from '@angular/core';
import { CrmDependencies, CrmInjectionToken, CrmObjectService, ObjectType } from '@galaxy/crm/static';
import { of, switchMap, tap, catchError, Observable, map, take, combineLatest, withLatestFrom } from 'rxjs';
import {
  ArchiveFieldSchemaRequestInterface,
  CRMFieldSchemaApiService,
  FieldSchemaInterface,
  CRMFieldSchemaCustomizationApiService,
  FieldSchemaCustomizationInterface,
} from '@vendasta/crm';
import { toObservable } from '@angular/core/rxjs-interop';

export const FIELD_OPERATION_STATUS = {
  SUCCESS: 'success',
  FIELD_CREATION_FAILED: 'field_creation_failed',
  FIELD_CREATED_CUSTOMIZATION_FAILED: 'field_created_customization_failed',
  FIELD_UPDATE_FAILED: 'field_update_failed',
  FIELD_UPDATED_CUSTOMIZATION_FAILED: 'field_updated_customization_failed',
  FIELD_DELETION_FAILED: 'field_deletion_failed',
} as const;

export type FieldOperationStatus = (typeof FIELD_OPERATION_STATUS)[keyof typeof FIELD_OPERATION_STATUS];

@Injectable()
export class CustomFieldsManagementTableChangesService {
  private readonly deletedFieldIds = signal<Map<string, string[]>>(new Map());
  private readonly createdFields = signal<Map<string, FieldSchemaInterface[]>>(new Map());
  private readonly updatedFields = signal<Map<string, FieldSchemaInterface[]>>(new Map());

  private deletedFieldIds$ = toObservable(this.deletedFieldIds);
  private createdFields$ = toObservable(this.createdFields);
  private updatedFields$ = toObservable(this.updatedFields);
  changes$ = combineLatest([this.deletedFieldIds$, this.createdFields$, this.updatedFields$]).pipe(
    map(([deletedFieldIds, createdFields, updatedFields]) => ({
      deletedFieldIds,
      createdFields,
      updatedFields,
    })),
  );

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly crmFieldSchemaApiService: CRMFieldSchemaApiService,
    private readonly crmFieldCustomizationApiService: CRMFieldSchemaCustomizationApiService,
    private readonly crmObjectService: CrmObjectService,
  ) {}

  private getObjectType(objectType: ObjectType, objectSubtype?: string): string {
    return objectSubtype ? `${objectType}:${objectSubtype}` : objectType;
  }

  public createField(
    objectType: ObjectType,
    field: FieldSchemaInterface,
    fieldCustomization?: FieldSchemaCustomizationInterface,
    objectSubtype?: string,
  ): Observable<FieldOperationStatus> {
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        return this.crmFieldSchemaApiService.createFieldSchema({
          namespace: namespace,
          crmObjectType: objectType,
          crmObjectSubtype: objectSubtype,
          fieldSchema: {
            ...field,
            namespace,
          },
        });
      }),
      withLatestFrom(this.config.namespace$),
      switchMap(([resp, namespace]) => {
        // Get the created field
        const getField$ = this.crmObjectService.getMultiFieldSchema(objectType, [resp.fieldId], objectSubtype);

        // If no customization is needed, just return the field creation result
        if (!fieldCustomization?.restrictedFieldValues?.length) {
          return getField$.pipe(
            tap((getResponse) =>
              this.createdFields.update((fieldMap) => {
                const fieldsPerObjectType = fieldMap.get(this.getObjectType(objectType, objectSubtype)) ?? [];
                if (getResponse?.fieldSchemas?.[0]) {
                  fieldMap.set(this.getObjectType(objectType, objectSubtype), [
                    ...fieldsPerObjectType,
                    getResponse.fieldSchemas[0],
                  ]);
                }
                return new Map(fieldMap);
              }),
            ),
            map((response) =>
              response != null ? FIELD_OPERATION_STATUS.SUCCESS : FIELD_OPERATION_STATUS.FIELD_CREATION_FAILED,
            ),
          );
        }

        // Create the field customization
        return getField$.pipe(
          switchMap((getResponse) => {
            if (!getResponse?.fieldSchemas?.[0]) {
              return of(FIELD_OPERATION_STATUS.FIELD_CREATION_FAILED);
            }

            const customization: FieldSchemaCustomizationInterface = {
              namespace,
              fieldId: resp.fieldId,
              restrictedFieldValues: fieldCustomization.restrictedFieldValues,
            };

            return this.crmFieldCustomizationApiService
              .createFieldSchemaCustomization({
                namespace,
                crmObjectType: objectType,
                fieldSchemaCustomization: customization,
              })
              .pipe(
                map(() => {
                  // Update the created fields tracking
                  this.createdFields.update((fieldMap) => {
                    const fieldsPerObjectType = fieldMap.get(this.getObjectType(objectType, objectSubtype)) ?? [];
                    if (getResponse?.fieldSchemas?.[0]) {
                      fieldMap.set(this.getObjectType(objectType, objectSubtype), [
                        ...fieldsPerObjectType,
                        getResponse.fieldSchemas[0],
                      ]);
                    }
                    return new Map(fieldMap);
                  });
                  return FIELD_OPERATION_STATUS.SUCCESS;
                }),
                catchError((error) => {
                  console.error('Failed to create field customization:', error);
                  // Field was created but customization failed
                  this.createdFields.update((fieldMap) => {
                    const fieldsPerObjectType = fieldMap.get(this.getObjectType(objectType, objectSubtype)) ?? [];
                    if (getResponse?.fieldSchemas?.[0]) {
                      fieldMap.set(this.getObjectType(objectType, objectSubtype), [
                        ...fieldsPerObjectType,
                        getResponse.fieldSchemas[0],
                      ]);
                    }
                    return new Map(fieldMap);
                  });
                  return of(FIELD_OPERATION_STATUS.FIELD_CREATED_CUSTOMIZATION_FAILED);
                }),
              );
          }),
        );
      }),
      catchError((error) => {
        console.error('Failed to create field:', error);
        return of(FIELD_OPERATION_STATUS.FIELD_CREATION_FAILED);
      }),
      take(1),
    );
  }

  public updateField(
    objectType: ObjectType,
    field: FieldSchemaInterface,
    fieldCustomization?: FieldSchemaCustomizationInterface,
    isSystemField = false,
    objectSubtype?: string,
  ): Observable<FieldOperationStatus> {
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        // For system fields, skip field schema update and only handle customization
        if (isSystemField) {
          return this.updateFieldCustomizationOnly(objectType, field.fieldId || '', fieldCustomization);
        }

        const updateFieldSchema$ = this.crmFieldSchemaApiService.updateFieldSchema({
          namespace: namespace,
          crmObjectType: objectType,
          crmObjectSubtype: objectSubtype,
          fieldSchema: {
            ...field,
            namespace,
          },
          fieldMask: {
            paths: ['fieldName', 'fieldDescription', 'externalId', 'readonly'],
          },
        });

        // If no field customization is provided, just update the field schema
        if (!fieldCustomization) {
          return updateFieldSchema$.pipe(
            map((result) => (result ? FIELD_OPERATION_STATUS.SUCCESS : FIELD_OPERATION_STATUS.FIELD_UPDATE_FAILED)),
          );
        }

        // Get the existing field customization, if any
        const getFieldCustomization$ = this.crmFieldCustomizationApiService
          .getMultiFieldSchemaCustomization({
            namespace: namespace,
            crmObjectType: objectType,
            fieldId: [field.fieldId || ''],
          })
          .pipe(
            map((response) => response?.fieldSchemaCustomizations?.[0] || null),
            catchError((error) => {
              console.error('Failed to get existing field customization:', error);
              return of(null);
            }),
          );

        // After updating the field schema, handle the customization
        return updateFieldSchema$.pipe(
          switchMap((updateResult) => {
            if (!updateResult) {
              return of(FIELD_OPERATION_STATUS.FIELD_UPDATE_FAILED);
            }

            return getFieldCustomization$.pipe(
              switchMap((existingCustomization) => {
                // Check if existing customization belongs to current namespace (not a default/fallback)
                const hasValidExistingCustomization =
                  existingCustomization && existingCustomization.namespace === namespace;

                // Only proceed with customization if there are restrictedFieldValues
                if (!fieldCustomization?.restrictedFieldValues?.length) {
                  // If there's a valid existing customization but no restricted values, delete it
                  if (hasValidExistingCustomization) {
                    return this.crmFieldCustomizationApiService
                      .deleteFieldSchemaCustomization({
                        namespace,
                        crmObjectType: objectType,
                        fieldId: field.fieldId || '',
                      })
                      .pipe(
                        map(() => FIELD_OPERATION_STATUS.SUCCESS),
                        catchError((error) => {
                          console.error('Failed to delete field customization:', error);
                          return of(FIELD_OPERATION_STATUS.FIELD_UPDATED_CUSTOMIZATION_FAILED);
                        }),
                      );
                  }
                  return of(FIELD_OPERATION_STATUS.SUCCESS);
                }

                const customization: FieldSchemaCustomizationInterface = {
                  namespace,
                  fieldId: field.fieldId || '',
                  restrictedFieldValues: fieldCustomization.restrictedFieldValues,
                };

                // If a valid existing customization was found (same namespace), update it
                if (hasValidExistingCustomization) {
                  return this.crmFieldCustomizationApiService
                    .updateFieldSchemaCustomization({
                      namespace,
                      crmObjectType: objectType,
                      fieldSchemaCustomization: customization,
                      fieldMask: {
                        paths: ['RestrictedFieldValues'],
                      },
                    })
                    .pipe(
                      map(() => FIELD_OPERATION_STATUS.SUCCESS),
                      catchError((error) => {
                        console.error('Failed to update field customization:', error);
                        return of(FIELD_OPERATION_STATUS.FIELD_UPDATED_CUSTOMIZATION_FAILED);
                      }),
                    );
                }

                // Otherwise create a new customization (either no existing or different namespace)
                return this.crmFieldCustomizationApiService
                  .createFieldSchemaCustomization({
                    namespace,
                    crmObjectType: objectType,
                    fieldSchemaCustomization: customization,
                  })
                  .pipe(
                    map(() => FIELD_OPERATION_STATUS.SUCCESS),
                    catchError((error) => {
                      console.error('Failed to create field customization:', error);
                      return of(FIELD_OPERATION_STATUS.FIELD_UPDATED_CUSTOMIZATION_FAILED);
                    }),
                  );
              }),
            );
          }),
        );
      }),
      catchError((error) => {
        console.error('Failed to update field:', error);
        return of(FIELD_OPERATION_STATUS.FIELD_UPDATE_FAILED);
      }),
      take(1),
      tap((result) => {
        // Only update the fields tracking for non-system fields and successful updates
        if (
          !isSystemField &&
          (result === FIELD_OPERATION_STATUS.SUCCESS ||
            result === FIELD_OPERATION_STATUS.FIELD_UPDATED_CUSTOMIZATION_FAILED)
        ) {
          this.updatedFields.update((fieldMap) => {
            const fieldsPerObjectType = fieldMap.get(this.getObjectType(objectType, objectSubtype)) ?? [];
            const index = fieldsPerObjectType.findIndex((f) => f.fieldId === field.fieldId);
            if (index !== -1) {
              fieldsPerObjectType[index] = field;
              fieldMap.set(this.getObjectType(objectType, objectSubtype), fieldsPerObjectType);
            } else {
              fieldMap.set(this.getObjectType(objectType, objectSubtype), [...fieldsPerObjectType, field]);
            }
            return new Map(fieldMap);
          });
        }
      }),
    );
  }

  /**
   * Updates only the field customization (restricted values) without modifying the field schema.
   * This is used for system fields where the schema cannot be modified.
   */
  public updateFieldCustomizationOnly(
    objectType: ObjectType,
    fieldId: string,
    fieldCustomization?: FieldSchemaCustomizationInterface,
  ): Observable<FieldOperationStatus> {
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        // Get the existing field customization, if any
        const getFieldCustomization$ = this.crmFieldCustomizationApiService
          .getMultiFieldSchemaCustomization({
            namespace: namespace,
            crmObjectType: objectType,
            fieldId: [fieldId],
          })
          .pipe(
            map((response) => response?.fieldSchemaCustomizations?.[0] || null),
            catchError((error) => {
              console.error('Failed to get existing field customization for system field:', error);
              return of(null);
            }),
          );

        return getFieldCustomization$.pipe(
          switchMap((existingCustomization) => {
            // Check if existing customization belongs to current namespace (not a default/fallback)
            const hasValidExistingCustomization =
              existingCustomization && existingCustomization.namespace === namespace;

            // If no customization is provided or no restricted values, remove existing customization
            if (!fieldCustomization?.restrictedFieldValues?.length) {
              // If there's a valid existing customization but no restricted values, delete it
              if (hasValidExistingCustomization) {
                return this.crmFieldCustomizationApiService
                  .deleteFieldSchemaCustomization({
                    namespace,
                    crmObjectType: objectType,
                    fieldId: fieldId,
                  })
                  .pipe(
                    map(() => FIELD_OPERATION_STATUS.SUCCESS),
                    catchError((error) => {
                      console.error('Failed to delete system field customization:', error);
                      return of(FIELD_OPERATION_STATUS.FIELD_UPDATED_CUSTOMIZATION_FAILED);
                    }),
                  );
              }
              return of(FIELD_OPERATION_STATUS.SUCCESS);
            }

            const customization: FieldSchemaCustomizationInterface = {
              namespace,
              fieldId: fieldId,
              restrictedFieldValues: fieldCustomization.restrictedFieldValues,
            };

            // If a valid existing customization was found (same namespace), update it
            if (hasValidExistingCustomization) {
              return this.crmFieldCustomizationApiService
                .updateFieldSchemaCustomization({
                  namespace,
                  crmObjectType: objectType,
                  fieldSchemaCustomization: customization,
                  fieldMask: {
                    paths: ['RestrictedFieldValues'],
                  },
                })
                .pipe(
                  map(() => FIELD_OPERATION_STATUS.SUCCESS),
                  catchError((error) => {
                    console.error('Failed to update system field customization:', error);
                    return of(FIELD_OPERATION_STATUS.FIELD_UPDATED_CUSTOMIZATION_FAILED);
                  }),
                );
            }

            // Otherwise create a new customization (either no existing or different namespace)
            return this.crmFieldCustomizationApiService
              .createFieldSchemaCustomization({
                namespace,
                crmObjectType: objectType,
                fieldSchemaCustomization: customization,
              })
              .pipe(
                map(() => FIELD_OPERATION_STATUS.SUCCESS),
                catchError((error) => {
                  console.error('Failed to create system field customization:', error);
                  return of(FIELD_OPERATION_STATUS.FIELD_UPDATED_CUSTOMIZATION_FAILED);
                }),
              );
          }),
        );
      }),
      catchError((error) => {
        console.error('Failed to update system field customization:', error);
        return of(FIELD_OPERATION_STATUS.FIELD_UPDATE_FAILED);
      }),
      take(1),
    );
  }

  public deleteField(request: ArchiveFieldSchemaRequestInterface): Observable<FieldOperationStatus> {
    return this.crmFieldSchemaApiService.archiveFieldSchema(request).pipe(
      catchError((error) => {
        console.error('Failed to delete field:', error);
        return of(null);
      }),
      map((response) => {
        return response != null ? FIELD_OPERATION_STATUS.SUCCESS : FIELD_OPERATION_STATUS.FIELD_DELETION_FAILED;
      }),
      take(1),
      tap((result) => {
        if (result === FIELD_OPERATION_STATUS.SUCCESS) {
          this.deletedFieldIds.update((fieldMap) => {
            const fieldsPerObjectType =
              fieldMap.get(this.getObjectType(request?.crmObjectType as ObjectType, request?.crmObjectSubtype)) ?? [];
            const index = fieldsPerObjectType.findIndex((fieldId) => fieldId === request.fieldId);
            if (index === -1 && request.fieldId) {
              fieldMap.set(this.getObjectType(request?.crmObjectType as ObjectType, request?.crmObjectSubtype), [
                ...fieldsPerObjectType,
                request.fieldId ?? '',
              ]);
            }
            return new Map(fieldMap);
          });
        }
      }),
    );
  }

  public getFieldCustomization(
    objectType: ObjectType,
    fieldId: string,
  ): Observable<FieldSchemaCustomizationInterface | null> {
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        return this.crmFieldCustomizationApiService.getMultiFieldSchemaCustomization({
          namespace: namespace,
          crmObjectType: objectType,
          fieldId: [fieldId],
        });
      }),
      map((response) => response?.fieldSchemaCustomizations?.[0] || null),
      catchError(() => of(null)),
      take(1),
    );
  }

  public resetChanges(objectType: ObjectType, objectSubtype?: string): void {
    const objectTypeSubtype = this.getObjectType(objectType, objectSubtype);
    const deletedFieldIds = this.deletedFieldIds();
    deletedFieldIds.set(objectTypeSubtype, []);
    const createdFields = this.createdFields();
    createdFields.set(objectTypeSubtype, []);
    const updatedFields = this.updatedFields();
    updatedFields.set(objectTypeSubtype, []);
    this.deletedFieldIds.set(new Map(deletedFieldIds));
    this.createdFields.set(new Map(createdFields));
    this.updatedFields.set(new Map(updatedFields));
  }
}
